/** "Copy for Claude Code" and its settings snippet: docs/infra/swa-bundle.md § Connect with Claude Code. */

import type { SwaBundle } from "./swa";

/** The only registry fields the block reads: none of them is secret, and nothing else can reach it. */
export type ClaudeCodeInputs = Pick<
	SwaBundle,
	| "projectNumber"
	| "siteUrl"
	| "postgresHost"
	| "subscriptionId"
	| "resourceGroup"
	| "backendName"
>;

/** The variable names in a bundle's `.env` (infra/bundles/swa/secrets.tf `local.app_env`); never their values. */
export const SWA_APP_ENV_VARIABLES = [
	"DATABASE_URL",
	"PGHOST",
	"PGPORT",
	"PGDATABASE",
	"PGUSER",
	"PGPASSWORD",
	"PGSSLMODE",
	"SWA_CLI_DEPLOYMENT_TOKEN",
	"SWA_URL",
	"SWA_VISITOR_PASSWORD",
] as const;

/** The bundle's one database (infra/bundles/swa/locals.tf `postgres_db`). */
export const SWA_DATABASE_NAME = "app";

/** Pinned like the provision workflow's placeholder deploy. */
const SWA_CLI = "@azure/static-web-apps-cli@2.0.10";

/** Loads `.env` for one command only, and runs nothing if loading fails (each Claude Code shell command starts bare). */
const WITH_ENV = "( set -a && . ./.env && set +a &&";

/** The password-free block to paste into Claude Code, or undefined while the registry lacks a field it names. */
export function claudeCodeBlock(bundle: ClaudeCodeInputs): string | undefined {
	// Named one by one: a property on the argument that is not listed here can never reach the text.
	const { projectNumber, siteUrl, postgresHost, subscriptionId, resourceGroup, backendName } =
		bundle;
	if (!siteUrl || !postgresHost || !subscriptionId || !resourceGroup || !backendName) {
		return undefined;
	}
	return [
		`This folder is the web app for project ${projectNumber}. Its secrets are in ./.env and must never enter this conversation or its transcript.`,
		"",
		"Rules",
		"- Never print, echo, cat, grep, log or commit .env or any value from it. Never put a value on a command line, in code, in a file or in an app setting.",
		"- Shell state does not persist between commands, so load .env inside each command that needs it, like this:",
		`    ${WITH_ENV} <command> )`,
		`- .env defines (names only): ${SWA_APP_ENV_VARIABLES.join(", ")}.`,
		"- Do not store personal or confidential data in the database until the data ceiling for web apps is published.",
		"",
		`Database: PostgreSQL 16 at ${postgresHost}, database ${SWA_DATABASE_NAME}. It is reachable from this laptop only while my IP is allowed.`,
		`    ${WITH_ENV} psql "$DATABASE_URL" -c "select 1" )`,
		"",
		`Front end: ${siteUrl}. Build the static files into ./dist, then deploy them (the CLI reads SWA_CLI_DEPLOYMENT_TOKEN from the environment):`,
		`    ${WITH_ENV} npx --yes ${SWA_CLI} deploy ./dist --env production --no-use-keychain )`,
		"Pass no --api-location: the API is the linked backend below, not a managed function.",
		"",
		`API: the App Service web app ${backendName} (Node 22, resource group ${resourceGroup}, subscription ${subscriptionId}). The site proxies ${siteUrl}/api to it, so every route keeps the /api prefix.`,
		"- Listen on process.env.PORT. Read the database settings from the app's environment: PGHOST, PGDATABASE, PGUSER, PGPASSWORD, PGPORT and PGSSLMODE. App Service fills them from Key Vault references, so never copy a secret into code or app settings. Connect with TLS and certificate verification.",
		"- Package the app with its production node_modules (npm ci --omit=dev), zip it, and deploy with my own Azure login (az login), not .env:",
		`    az webapp deploy --subscription ${subscriptionId} --resource-group ${resourceGroup} --name ${backendName} --src-path api.zip --type zip`,
		"- Never change the app's settings, turn on basic authentication or open a console or SSH session on it.",
		"",
		`The site asks for a visitor password. Do not sign in or ask me for it: tell me what to check in my browser at ${siteUrl} and I will report back.`,
		"",
	].join("\n");
}

/** The `.claude/settings.json` that denies reading or editing `.env`, and the `.gitignore` line that keeps it out of git. */
export function claudeCodeSettings(): { settingsJson: string; gitignoreLine: string } {
	const deny = ["Read(./.env)", "Read(./.env.*)", "Edit(./.env)", "Edit(./.env.*)"];
	return {
		settingsJson: `${JSON.stringify({ permissions: { deny } }, null, 2)}\n`,
		gitignoreLine: ".env",
	};
}
